import { ArrowRight, ChartColumn, Compass, type LucideIcon, Target } from "lucide-react";
import { Link } from "react-router";

import { Eyebrow } from "@/components/eyebrow";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ReportPreview } from "./report-preview";

const WHAT_YOU_GET: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: ChartColumn,
    title: "A scorecard.",
    body: "Business model, operating model, and structural protector scores, with an overall exposure level and confidence.",
  },
  {
    icon: Compass,
    title: "A strategic position.",
    body: "Full Transformation, Workflow Redesign, Strategic Reinvention, or Fortify & Monitor, plus why you landed there.",
  },
  {
    icon: Target,
    title: "The next move.",
    body: "A clear diagnosis, the assumptions to validate, and three to five practical actions to take next.",
  },
];

export function LandingPage() {
  return (
    <PageShell wide logoTo="/">
      <section>
        <Eyebrow>AI Disruption Exposure Report</Eyebrow>
        <h1 className="text-primary mt-3 max-w-[14ch] text-5xl leading-[1.05] font-normal tracking-tight sm:text-6xl lg:text-7xl">
          Everyone measures AI risk.{" "}
          <span className="text-accent">We tell you what to do about it.</span>
        </h1>
        <p className="text-muted-foreground mt-6 max-w-2xl text-lg leading-relaxed sm:text-xl">
          Answer a short diagnostic and get a company-specific read on your AI disruption exposure:
          a scorecard, your strategic position, and the next move. A BOI expert reviews your report
          in under one business day.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/assess">
              Start the assessment <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <ReportPreview />

      <section>
        <Eyebrow className="mb-4">What you get</Eyebrow>
        <div className="grid gap-4 sm:grid-cols-3">
          {WHAT_YOU_GET.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border-accent">
              <CardContent className="pt-6">
                <Icon className="text-accent h-7 w-7" strokeWidth={1.75} />
                <h3 className="text-primary mt-3 text-2xl font-normal tracking-tight">{title}</h3>
                <p className="text-muted mt-2 text-sm leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <Link
          to="/assess"
          className="text-accent inline-flex items-center gap-1.5 font-bold hover:underline"
        >
          Drop in your strategy docs and we’ll prefill the questions{" "}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </PageShell>
  );
}
